import React, {useEffect, useState} from 'react';
import {api, useApiContext} from '../api';
import {OnChangeFieldValue} from '../field/FieldValue';
import {Actions} from '../types/Action';
import {Field} from '../types/Field';
import {RelatedResources, RelatedSchemas} from '../types/Resource';
import {ResourceDetails} from '../types/ResourceDetails';
import {array} from '../util';
import {ItemWidget, USE_DEFAULT_LABEL} from '../widgets/ItemWidget';
import {useItemProps} from './hooks/useItemProps';
import {useOnChange} from './hooks/useOnChange';
import {useOnClear} from './hooks/useOnClear';
import {widgetProps} from '../widgets/common';

type Props = {
  value: any;
  onChange: OnChangeFieldValue;
  field: Field;
  item?: ResourceDetails;
  actions: Actions;
  relatedResources?: RelatedResources;
  relatedSchemas?: RelatedSchemas;
  capacity?: number;
  selectButtonLabel?: string;
  hideClearButton?: boolean;
  narrow?: boolean;
};

export function ItemField(props: Props): JSX.Element | null {
  const ids = excludeEmpty(array(props.value));
  const itemProps = useItemProps(
    props.field,
    props.item,
    props.relatedSchemas,
    props.capacity,
  );
  const items = useItems(props.field.itemType, props.relatedResources);
  const onChange = useOnChange(props.onChange, props.field.id);
  const onClear = useOnClear(props.onChange, props.field.id);
  const selectButtonLabel =
    props.selectButtonLabel ??
    widgetProps<string>(props.field, 'select_button_label', USE_DEFAULT_LABEL);

  const hideClearButton =
    props.hideClearButton ??
    widgetProps<boolean>(props.field, 'hide_clear_button', false);

  const enableTextInput = widgetProps<boolean>(
    props.field,
    'enable_text_input',
    false,
  );

  const renderAsPlaneText = widgetProps<boolean>(
    props.field,
    'render_as_plane_text',
    false,
  );

  if (!itemProps || !items) {
    return null;
  }

  return (
    <ItemWidget
      {...props}
      {...itemProps}
      {...items}
      value={ids}
      onChange={onChange}
      onClear={onClear}
      selectButtonLabel={selectButtonLabel}
      hideClearButton={hideClearButton}
      enableTextInput={enableTextInput}
      renderAsPlaneText={renderAsPlaneText}
      narrow={props.narrow}
    />
  );
}

type Items = {
  useIdsToItems: (ids: string[]) => ResourceDetails[];
  useItemsToIds: (items: ResourceDetails[]) => string[];
};

function useItems(
  schemaId?: string,
  relatedResources?: RelatedResources,
): Items | null {
  const [relates, setRelates] = useState<RelatedResources>({
    ...relatedResources,
  });
  const ctx = useApiContext();

  if (!schemaId) {
    return null;
  }

  const useItemsToIds = (v: ResourceDetails[]): string[] => {
    const ids = array(v).map((x) => x.id);
    const newRelates = {...relates};

    for (let x of array(v)) {
      newRelates[x.id] = x;
    }

    setRelates(newRelates);
    return ids;
  };

  const useIdsToItems = (ids: string[]): ResourceDetails[] => {
    useEffect(() => {
      (async () => {
        const newRelates = {...relates};
        let changed = false;

        for (let id of ids) {
          if (newRelates[id]) {
            continue;
          }

          try {
            const v = await api.show(ctx, schemaId, id);
            newRelates[id] = v.details;
            changed = true;
          } catch (ignore) {}
        }

        if (changed) {
          setRelates(newRelates);
        }
      })();
    }, [ids]);

    return items(ids, relates);
  };

  return {useItemsToIds, useIdsToItems};
}

function items(ids: string[], relates: RelatedResources) {
  return [...new Set(relatedItems(ids, relates))];
}

export function relatedItems(
  ids: string[],
  relates?: RelatedResources,
): ResourceDetails[] {
  if (!relates) {
    return [];
  }

  return ids.map((id) => relates[id]).filter((item) => !!item);
}

function excludeEmpty(ids: string[]): string[] {
  return ids.filter((id) => !!id);
}
